import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    DeleteOutlined,
    EditFilled,
    PlusOutlined,
    DownOutlined,
    UpOutlined,
    FileTextOutlined,
    TagsOutlined,
    FolderOutlined,
    SearchOutlined,
    FilterOutlined,
    ReloadOutlined,
    EyeOutlined,
    StarOutlined,
    CalendarOutlined,
    UserOutlined,
    GlobalOutlined,
} from '@ant-design/icons';
import {
    Button,
    Table,
    Modal,
    Switch,
    Select,
    Input,
    Space,
    Card,
    Collapse,
    Tag,
    Tooltip,
    message,
    Popconfirm,
    Badge,
    Divider,
    Typography,
    Row,
    Col,
    Statistic,
    Progress,
    Empty,
    Spin,
} from 'antd';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import {
    fetchBlogs,
    createBlog,
    updateBlog,
    deleteBlog,
    fetchBlogCategories,
    createBlogCategory,
    updateBlogCategory,
    deleteBlogCategory,
    fetchBlogTags,
    createBlogTag,
    updateBlogTag,
    deleteBlogTag,
    setPagination,
    setFilters,
    setSort,
    clearError,
} from '../../redux/slice/blog.slice';
import BlogForm from './blog/BlogForm';
import CategoryForm from './blog/CategoryForm';
import TagForm from './blog/TagForm';
import DebouncedSearch from '../Common/DebouncedSearch';

const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;
const { Title, Text } = Typography;

export default function Blog() {
    const dispatch = useDispatch();
    const { blogs, categories, tags, loading, error, pagination, filters, sort } =
        useSelector((state) => state.blog);

    // Ensure categories and tags are always arrays
    const safeCategories = Array.isArray(categories) ? categories : [];
    const safeTags = Array.isArray(tags) ? tags : [];

    // Debug logging
    console.log('Categories:', categories, 'Type:', typeof categories);
    console.log('Tags:', tags, 'Type:', typeof tags);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingTag, setEditingTag] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    useEffect(() => {
        dispatch(fetchBlogs({ ...pagination, ...filters, ...sort }));
        dispatch(fetchBlogCategories());
        dispatch(fetchBlogTags());
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            message.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleBlogSubmit = async (data) => {
        try {
            if (editingBlog) {
                await dispatch(
                    updateBlog({ id: editingBlog._id, blogData: data }),
                ).unwrap();
                message.success('Blog updated successfully');
            } else {
                await dispatch(createBlog(data)).unwrap();
                message.success('Blog created successfully');
            }
            setIsModalOpen(false);
            setEditingBlog(null);
            dispatch(fetchBlogs({ ...pagination, ...filters, ...sort }));
        } catch (err) {
            message.error(err.message || 'Operation failed');
        }
    };

    const handleCategorySubmit = async (data) => {
        try {
            if (editingCategory) {
                await dispatch(
                    updateBlogCategory({ id: editingCategory._id, categoryData: data }),
                ).unwrap();
                message.success('Category updated successfully');
            } else {
                await dispatch(createBlogCategory(data)).unwrap();
                message.success('Category created successfully');
            }
            setIsCategoryModalOpen(false);
            setEditingCategory(null);
            dispatch(fetchBlogCategories());
        } catch (err) {
            message.error(err.message || 'Operation failed');
        }
    };

    const handleTagSubmit = async (data) => {
        try {
            if (editingTag) {
                await dispatch(
                    updateBlogTag({ id: editingTag._id, tagData: data }),
                ).unwrap();
                message.success('Tag updated successfully');
            } else {
                await dispatch(createBlogTag(data)).unwrap();
                message.success('Tag created successfully');
            }
            setIsTagModalOpen(false);
            setEditingTag(null);
            dispatch(fetchBlogTags());
        } catch (err) {
            message.error(err.message || 'Operation failed');
        }
    };

    const handleDeleteBlog = async (id) => {
        try {
            await dispatch(deleteBlog(id)).unwrap();
            message.success('Blog deleted successfully');
            dispatch(fetchBlogs({ ...pagination, ...filters, ...sort }));
        } catch (err) {
            message.error(err.message || 'Delete failed');
        }
    };

    const handleDeleteCategory = async (id) => {
        try {
            await dispatch(deleteBlogCategory(id)).unwrap();
            message.success('Category deleted successfully');
            dispatch(fetchBlogCategories());
        } catch (err) {
            message.error(err.message || 'Delete failed');
        }
    };

    const handleDeleteTag = async (id) => {
        try {
            await dispatch(deleteBlogTag(id)).unwrap();
            message.success('Tag deleted successfully');
            dispatch(fetchBlogTags());
        } catch (err) {
            message.error(err.message || 'Delete failed');
        }
    };

    const handleSearch = (keywords) => {
        const newFilters = { ...filters, keywords };
        dispatch(setFilters(newFilters));
        dispatch(fetchBlogs({ ...pagination, ...newFilters, ...sort }));
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        dispatch(setFilters(newFilters));
        dispatch(fetchBlogs({ ...pagination, ...newFilters, ...sort }));
    };

    const handleTableChange = (pagination, filters, sorter) => {
        const newPagination = {
            ...pagination,
            page: pagination.current,
            limit: pagination.pageSize,
        };
        const newSort = {
            sortBy: sorter.field || 'published_at',
            sortOrder: sorter.order === 'ascend' ? 1 : -1,
        };

        dispatch(setPagination(newPagination));
        dispatch(setSort(newSort));
        dispatch(fetchBlogs({ ...newPagination, ...filters, ...newSort }));
    };

    const handleRefresh = () => {
        dispatch(fetchBlogs({ ...pagination, ...filters, ...sort }));
        dispatch(fetchBlogCategories());
        dispatch(fetchBlogTags());
        message.success('Data refreshed successfully');
    };

    // Calculate statistics
    const totalBlogs = blogs?.length || 0;
    const publishedBlogs =
        blogs?.filter((blog) => blog.status === 'published').length || 0;
    const draftBlogs = blogs?.filter((blog) => blog.status === 'draft').length || 0;
    const featuredBlogs = blogs?.filter((blog) => blog.is_featured).length || 0;

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: true,
            width: 300,
            render: (text, record) => (
                <div className="max-w-xs">
                    <div className="font-medium text-gray-900 truncate">{text}</div>
                    <div className="text-xs text-gray-500 flex items-center mt-1">
                        <GlobalOutlined className="mr-1" />
                        {record.slug}
                    </div>
                    {record.is_featured && (
                        <Tag color="gold" className="mt-1">
                            <StarOutlined /> Featured
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'Category',
            dataIndex: 'categories',
            key: 'categories',
            width: 120,
            render: (categories) =>
                categories && categories.length > 0 ? (
                    <Tag
                        color={categories[0].color || '#007bff'}
                        className="flex items-center"
                    >
                        <FolderOutlined className="mr-1" />
                        {categories[0].name}
                    </Tag>
                ) : (
                    <Text type="secondary">No category</Text>
                ),
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            key: 'tags',
            width: 150,
            render: (tags) => (
                <div>
                    {tags && tags.length > 0 ? (
                        <>
                            {tags.slice(0, 2).map((tag) => (
                                <Tag
                                    key={tag._id}
                                    color={tag.color || '#6c757d'}
                                    size="small"
                                    className="mb-1"
                                >
                                    <TagsOutlined className="mr-1" />
                                    {tag.name}
                                </Tag>
                            ))}
                            {tags.length > 2 && (
                                <Tag size="small" className="mb-1">
                                    +{tags.length - 2}
                                </Tag>
                            )}
                        </>
                    ) : (
                        <Text type="secondary">No tags</Text>
                    )}
                </div>
            ),
        },
        {
            title: 'Author',
            dataIndex: 'author',
            key: 'author',
            width: 120,
            render: (author) => (
                <div className="flex items-center">
                    <UserOutlined className="mr-1 text-gray-400" />
                    <span>{author || 'Unknown'}</span>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => {
                const statusConfig = {
                    draft: { color: 'orange', text: 'Draft', icon: '📝' },
                    published: { color: 'green', text: 'Published', icon: '✅' },
                    archived: { color: 'red', text: 'Archived', icon: '🗄️' },
                };
                const config = statusConfig[status] || {
                    color: 'default',
                    text: status,
                    icon: '❓',
                };
                return (
                    <Badge
                        status={config.color}
                        text={
                            <span className="flex items-center">
                                <span className="mr-1">{config.icon}</span>
                                {config.text}
                            </span>
                        }
                    />
                );
            },
        },
        {
            title: 'Published Date',
            dataIndex: 'published_at',
            key: 'published_at',
            width: 130,
            sorter: true,
            render: (date) =>
                date ? (
                    <div className="flex items-center">
                        <CalendarOutlined className="mr-1 text-gray-400" />
                        <span>{moment(date).format('MMM DD, YYYY')}</span>
                    </div>
                ) : (
                    <Text type="secondary">Not published</Text>
                ),
        },
        {
            title: 'Views',
            dataIndex: 'view_count',
            key: 'view_count',
            width: 80,
            render: (count) => (
                <div className="flex items-center">
                    <EyeOutlined className="mr-1 text-gray-400" />
                    <span>{count || 0}</span>
                </div>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Blog">
                        <Button
                            type="text"
                            icon={<EditFilled />}
                            onClick={() => {
                                setEditingBlog(record);
                                setIsModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Are you sure you want to delete this blog?"
                        description="This action cannot be undone."
                        onConfirm={() => handleDeleteBlog(record._id)}
                        okText="Yes, Delete"
                        cancelText="Cancel"
                        okType="danger"
                    >
                        <Tooltip title="Delete Blog">
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                className="hover:text-red-800"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="flex h-full bg-gray-50">
            {/* Sidebar */}
            <div
                className={`${
                    sidebarCollapsed ? 'w-16' : 'w-80'
                } bg-white border-r border-gray-200 transition-all duration-300 shadow-sm`}
            >
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center justify-between">
                        <h3
                            className={`font-semibold text-blue-800 ${
                                sidebarCollapsed ? 'hidden' : 'block'
                            }`}
                        >
                            <FileTextOutlined className="mr-2" />
                            Blog Management
                        </h3>
                        <Button
                            type="text"
                            icon={sidebarCollapsed ? <DownOutlined /> : <UpOutlined />}
                            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                            className="text-blue-600"
                        />
                    </div>
                </div>

                {!sidebarCollapsed && (
                    <div className="p-4 space-y-4">
                        {/* Statistics */}
                        <Card
                            size="small"
                            className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                        >
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Statistic
                                        title="Total Blogs"
                                        value={totalBlogs}
                                        valueStyle={{ color: '#3f6212' }}
                                        prefix={<FileTextOutlined />}
                                    />
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title="Published"
                                        value={publishedBlogs}
                                        valueStyle={{ color: '#059669' }}
                                        prefix={<span>✅</span>}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={16} className="mt-2">
                                <Col span={12}>
                                    <Statistic
                                        title="Drafts"
                                        value={draftBlogs}
                                        valueStyle={{ color: '#d97706' }}
                                        prefix={<span>📝</span>}
                                    />
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title="Featured"
                                        value={featuredBlogs}
                                        valueStyle={{ color: '#dc2626' }}
                                        prefix={<StarOutlined />}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* Categories Section */}
                        <Card
                            title={
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center text-green-700">
                                        <FolderOutlined className="mr-2" />
                                        Categories ({safeCategories.length})
                                    </span>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            setEditingCategory(null);
                                            setIsCategoryModalOpen(true);
                                        }}
                                        className="text-green-600 hover:text-green-800"
                                    />
                                </div>
                            }
                            size="small"
                            className="shadow-sm border-l-4 border-l-green-500"
                        >
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {safeCategories && safeCategories.length > 0 ? (
                                    safeCategories.map((category) => (
                                        <div
                                            key={category._id}
                                            className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center">
                                                <div
                                                    className="w-3 h-3 rounded-full mr-2"
                                                    style={{
                                                        backgroundColor:
                                                            category.color || '#007bff',
                                                    }}
                                                />
                                                <span className="text-sm font-medium">
                                                    {category.name}
                                                </span>
                                            </div>
                                            <Space size="small">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditFilled />}
                                                    onClick={() => {
                                                        setEditingCategory(category);
                                                        setIsCategoryModalOpen(true);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800"
                                                />
                                                <Popconfirm
                                                    title="Delete this category?"
                                                    description="This will also remove it from all associated blogs."
                                                    onConfirm={() =>
                                                        handleDeleteCategory(category._id)
                                                    }
                                                    okText="Delete"
                                                    cancelText="Cancel"
                                                    okType="danger"
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        className="hover:text-red-800"
                                                    />
                                                </Popconfirm>
                                            </Space>
                                        </div>
                                    ))
                                ) : (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="No categories"
                                        className="py-4"
                                    />
                                )}
                            </div>
                        </Card>

                        {/* Tags Section */}
                        <Card
                            title={
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center text-purple-700">
                                        <TagsOutlined className="mr-2" />
                                        Tags ({safeTags.length})
                                    </span>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            setEditingTag(null);
                                            setIsTagModalOpen(true);
                                        }}
                                        className="text-purple-600 hover:text-purple-800"
                                    />
                                </div>
                            }
                            size="small"
                            className="shadow-sm border-l-4 border-l-purple-500"
                        >
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {safeTags && safeTags.length > 0 ? (
                                    safeTags.map((tag) => (
                                        <div
                                            key={tag._id}
                                            className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center">
                                                <div
                                                    className="w-3 h-3 rounded-full mr-2"
                                                    style={{
                                                        backgroundColor:
                                                            tag.color || '#6c757d',
                                                    }}
                                                />
                                                <span className="text-sm font-medium">
                                                    {tag.name}
                                                </span>
                                            </div>
                                            <Space size="small">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditFilled />}
                                                    onClick={() => {
                                                        setEditingTag(tag);
                                                        setIsTagModalOpen(true);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800"
                                                />
                                                <Popconfirm
                                                    title="Delete this tag?"
                                                    description="This will also remove it from all associated blogs."
                                                    onConfirm={() =>
                                                        handleDeleteTag(tag._id)
                                                    }
                                                    okText="Delete"
                                                    cancelText="Cancel"
                                                    okType="danger"
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        className="hover:text-red-800"
                                                    />
                                                </Popconfirm>
                                            </Space>
                                        </div>
                                    ))
                                ) : (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="No tags"
                                        className="py-4"
                                    />
                                )}
                            </div>
                        </Card>
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="flex-1 p-6">
                <div className="mb-6">
                    {/* Header */}
                    <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <Title level={2} className="mb-1 flex items-center">
                                    <FileTextOutlined className="mr-3 text-blue-600" />
                                    Blog Management
                                </Title>
                                <Text type="secondary">
                                    Create, edit, and manage your blog posts, categories,
                                    and tags
                                </Text>
                            </div>
                            <div className="flex items-center space-x-3">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefresh}
                                    loading={loading}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    size="large"
                                    onClick={() => {
                                        setEditingBlog(null);
                                        setIsModalOpen(true);
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700"
                                >
                                    Add New Blog
                                </Button>
                            </div>
                        </div>

                        {/* Filters */}
                        <Card className="bg-gray-50 border-0">
                            <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700">
                                        <SearchOutlined className="mr-1" />
                                        Search
                                    </label>
                                    <DebouncedSearch
                                        placeholder="Search blogs by title, content..."
                                        onSearch={handleSearch}
                                        size="medium"
                                        width="100%"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700">
                                        <FilterOutlined className="mr-1" />
                                        Status
                                    </label>
                                    <Select
                                        placeholder="All Status"
                                        className="w-full"
                                        value={filters.status}
                                        onChange={(value) =>
                                            handleFilterChange('status', value)
                                        }
                                        allowClear
                                    >
                                        <Option value="draft">
                                            <div className="flex items-center">
                                                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                                                Draft
                                            </div>
                                        </Option>
                                        <Option value="published">
                                            <div className="flex items-center">
                                                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                                Published
                                            </div>
                                        </Option>
                                        <Option value="archived">
                                            <div className="flex items-center">
                                                <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                                                Archived
                                            </div>
                                        </Option>
                                    </Select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700">
                                        <FolderOutlined className="mr-1" />
                                        Category
                                    </label>
                                    <Select
                                        placeholder="All Categories"
                                        className="w-full"
                                        value={filters.category}
                                        onChange={(value) =>
                                            handleFilterChange('category', value)
                                        }
                                        allowClear
                                    >
                                        {safeCategories.map((category) => (
                                            <Option
                                                key={category._id}
                                                value={category._id}
                                            >
                                                <div className="flex items-center">
                                                    <div
                                                        className="w-3 h-3 rounded-full mr-2"
                                                        style={{
                                                            backgroundColor:
                                                                category.color,
                                                        }}
                                                    />
                                                    {category.name}
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700">
                                        <TagsOutlined className="mr-1" />
                                        Tags
                                    </label>
                                    <Select
                                        mode="multiple"
                                        placeholder="All Tags"
                                        className="w-full"
                                        value={filters.tags}
                                        onChange={(value) =>
                                            handleFilterChange('tags', value)
                                        }
                                        allowClear
                                    >
                                        {safeTags.map((tag) => (
                                            <Option key={tag._id} value={tag._id}>
                                                <div className="flex items-center">
                                                    <div
                                                        className="w-3 h-3 rounded-full mr-2"
                                                        style={{
                                                            backgroundColor: tag.color,
                                                        }}
                                                    />
                                                    {tag.name}
                                                </div>
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700">
                                        <UserOutlined className="mr-1" />
                                        Author
                                    </label>
                                    <Input
                                        placeholder="Search by author"
                                        value={filters.author}
                                        onChange={(e) =>
                                            handleFilterChange('author', e.target.value)
                                        }
                                        allowClear
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2 text-gray-700">
                                        <StarOutlined className="mr-1" />
                                        Featured
                                    </label>
                                    <Select
                                        placeholder="All"
                                        className="w-full"
                                        value={filters.is_featured}
                                        onChange={(value) =>
                                            handleFilterChange('is_featured', value)
                                        }
                                        allowClear
                                    >
                                        <Option value="">
                                            <div className="flex items-center">
                                                <span className="mr-2">📋</span>
                                                All
                                            </div>
                                        </Option>
                                        <Option value={true}>
                                            <div className="flex items-center">
                                                <StarOutlined className="mr-2 text-yellow-500" />
                                                Featured
                                            </div>
                                        </Option>
                                        <Option value={false}>
                                            <div className="flex items-center">
                                                <span className="mr-2">•</span>
                                                Not Featured
                                            </div>
                                        </Option>
                                    </Select>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Table */}
                    <Card className="shadow-sm">
                        <Table
                            columns={columns}
                            dataSource={blogs}
                            rowKey="_id"
                            loading={loading}
                            pagination={{
                                current: pagination.page,
                                pageSize: pagination.limit,
                                total: pagination.total,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} items`,
                                pageSizeOptions: ['10', '20', '50', '100'],
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: 1200 }}
                            className="custom-table"
                            locale={{
                                emptyText: (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="No blogs found"
                                    />
                                ),
                            }}
                        />
                    </Card>
                </div>
            </div>

            {/* Blog Form Modal */}
            <Modal
                title={null}
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    setEditingBlog(null);
                }}
                footer={null}
                width={1000}
                destroyOnClose
                className="blog-form-modal"
            >
                <BlogForm
                    onSubmit={handleBlogSubmit}
                    categories={safeCategories}
                    tags={safeTags}
                    initialData={editingBlog}
                    onCancel={() => {
                        setIsModalOpen(false);
                        setEditingBlog(null);
                    }}
                />
            </Modal>

            {/* Category Form Modal */}
            <Modal
                title={editingCategory ? 'Edit Category' : 'Create New Category'}
                open={isCategoryModalOpen}
                onCancel={() => {
                    setIsCategoryModalOpen(false);
                    setEditingCategory(null);
                }}
                footer={null}
                width={500}
                destroyOnClose
            >
                <CategoryForm
                    onSubmit={handleCategorySubmit}
                    initialData={editingCategory}
                    onCancel={() => {
                        setIsCategoryModalOpen(false);
                        setEditingCategory(null);
                    }}
                />
            </Modal>

            {/* Tag Form Modal */}
            <Modal
                title={editingTag ? 'Edit Tag' : 'Create New Tag'}
                open={isTagModalOpen}
                onCancel={() => {
                    setIsTagModalOpen(false);
                    setEditingTag(null);
                }}
                footer={null}
                width={500}
                destroyOnClose
            >
                <TagForm
                    onSubmit={handleTagSubmit}
                    initialData={editingTag}
                    onCancel={() => {
                        setIsTagModalOpen(false);
                        setEditingTag(null);
                    }}
                />
            </Modal>
        </div>
    );
}
